import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FiCreditCard, FiCheck, FiX, FiAlertTriangle, FiExternalLink, 
  FiSettings, FiRefreshCw, FiPercent, FiDollarSign, FiEye, FiEyeOff,
  FiSave, FiWifi, FiLink
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [myposConfig, setMyposConfig] = useState(null);
  
  // Payment settings form
  const [settings, setSettings] = useState({
    // MyPOS
    mypos_enabled: false,
    mypos_client_id: '',
    mypos_client_secret: '',
    mypos_wallet_number: '',
    mypos_test_mode: true,
    mypos_add_fees_to_client: false,
    mypos_fee_percentage: '1.5',
    
    // Bank Transfer
    bank_transfer_enabled: true,
    bank_name: '',
    bank_iban: '',
    bank_bic: '',
    bank_account_holder: '',
    
    // Deposit settings
    deposit_enabled: true,
    deposit_percentage: '30',
    deposit_type: 'percentage',
    
    // General
    currency: 'EUR',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSettings(),
        loadMyPOSConfig(),
        loadTransactions(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({
          ...prev,
          mypos_enabled: data.mypos_enabled || false,
          mypos_client_id: data.mypos_client_id || '',
          mypos_client_secret: data.mypos_client_secret || '',
          mypos_wallet_number: data.mypos_wallet_number || '',
          mypos_test_mode: data.mypos_test_mode !== false,
          mypos_add_fees_to_client: data.mypos_add_fees_to_client || false,
          mypos_fee_percentage: data.mypos_fee_percentage || '1.5',
          bank_transfer_enabled: data.bank_transfer_enabled !== false,
          bank_name: data.bank_name || '',
          bank_iban: data.bank_iban || '',
          bank_bic: data.bank_bic || '',
          bank_account_holder: data.bank_account_holder || '',
          deposit_enabled: data.deposit_enabled !== false,
          deposit_percentage: data.deposit_percentage || '30',
          deposit_type: data.deposit_type || 'percentage',
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadMyPOSConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/mypos/config`);
      if (res.ok) {
        setMyposConfig(await res.json());
      }
    } catch (error) {
      console.error('Error loading MyPOS config:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/payments/mypos/transactions?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTransactions(await res.json());
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/payments/mypos/transactions/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/settings/payments`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Paramètres enregistrés');
        loadMyPOSConfig();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/mypos/test-connection`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message || 'Connexion réussie !');
      } else {
        toast.error(data.error || 'Échec de la connexion');
      }
    } catch (error) {
      toast.error('Erreur lors du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-100 text-gray-600';
      case 'expired': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Payé';
      case 'pending': return 'En attente';
      case 'failed': return 'Échoué';
      case 'cancelled': return 'Annulé';
      case 'expired': return 'Expiré';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuration des Paiements</h1>
          <p className="text-gray-500">Gérez vos moyens de paiement et les paramètres d'acompte</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <FiRefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-500">Paiements réussis</div>
              <div className="text-2xl font-bold text-green-600">{stats.completed?.count || 0}</div>
              <div className="text-sm text-gray-400">{formatAmount(stats.completed?.amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-500">En attente</div>
              <div className="text-2xl font-bold text-amber-600">{stats.pending?.count || 0}</div>
              <div className="text-sm text-gray-400">{formatAmount(stats.pending?.amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-500">Échoués</div>
              <div className="text-2xl font-bold text-red-600">{stats.failed?.count || 0}</div>
              <div className="text-sm text-gray-400">{formatAmount(stats.failed?.amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-500">Annulés</div>
              <div className="text-2xl font-bold text-gray-600">{stats.cancelled?.count || 0}</div>
              <div className="text-sm text-gray-400">{formatAmount(stats.cancelled?.amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-500">Expirés</div>
              <div className="text-2xl font-bold text-gray-500">{stats.expired?.count || 0}</div>
              <div className="text-sm text-gray-400">{formatAmount(stats.expired?.amount)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="mypos" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
          <TabsTrigger value="mypos" className="gap-2">
            <FiCreditCard className="h-4 w-4" />
            MyPOS (CB)
          </TabsTrigger>
          <TabsTrigger value="bank" className="gap-2">
            <FiDollarSign className="h-4 w-4" />
            Virement
          </TabsTrigger>
          <TabsTrigger value="deposit" className="gap-2">
            <FiPercent className="h-4 w-4" />
            Acompte
          </TabsTrigger>
        </TabsList>

        {/* MyPOS Configuration */}
        <TabsContent value="mypos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FiCreditCard className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>MyPOS - Paiement par Carte Bancaire</CardTitle>
                    <CardDescription>Permettez à vos clients de payer par CB via un lien sécurisé</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings.mypos_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, mypos_enabled: checked }))}
                  />
                  <span className="text-sm text-gray-500">{settings.mypos_enabled ? 'Activé' : 'Désactivé'}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Connection Status */}
              <div className="flex flex-wrap gap-3">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                  myposConfig?.client_id_configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {myposConfig?.client_id_configured ? <FiCheck className="h-4 w-4" /> : <FiAlertTriangle className="h-4 w-4" />}
                  {myposConfig?.client_id_configured ? "ID configuré" : "ID manquant"}
                </div>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                  myposConfig?.client_secret_configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {myposConfig?.client_secret_configured ? <FiCheck className="h-4 w-4" /> : <FiAlertTriangle className="h-4 w-4" />}
                  {myposConfig?.client_secret_configured ? "Clé secrète configurée" : "Clé secrète manquante"}
                </div>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                  settings.mypos_test_mode ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                }`}>
                  <FiSettings className="h-4 w-4" />
                  {settings.mypos_test_mode ? 'Mode Test' : 'Mode Production'}
                </div>
              </div>

              {/* Configuration Form */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="mypos_client_id">ID Client MyPOS</Label>
                  <Input
                    id="mypos_client_id"
                    placeholder="Ex: SMiF36XVU9j7bLVdltOL07a5"
                    value={settings.mypos_client_id}
                    onChange={(e) => setSettings(prev => ({ ...prev, mypos_client_id: e.target.value }))}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="mypos_client_secret">Clé Secrète MyPOS</Label>
                  <div className="relative">
                    <Input
                      id="mypos_client_secret"
                      type={showSecrets ? "text" : "password"}
                      placeholder="Votre clé secrète MyPOS"
                      value={settings.mypos_client_secret}
                      onChange={(e) => setSettings(prev => ({ ...prev, mypos_client_secret: e.target.value }))}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecrets(!showSecrets)}
                    >
                      {showSecrets ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="mypos_wallet_number">Numéro de Wallet (optionnel)</Label>
                  <Input
                    id="mypos_wallet_number"
                    placeholder="Ex: 61938166610"
                    value={settings.mypos_wallet_number}
                    onChange={(e) => setSettings(prev => ({ ...prev, mypos_wallet_number: e.target.value }))}
                  />
                </div>

                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={settings.mypos_test_mode}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, mypos_test_mode: checked }))}
                    />
                    <Label>Mode Test</Label>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-medium">Options de frais</h4>
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={settings.mypos_add_fees_to_client}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, mypos_add_fees_to_client: checked }))}
                    />
                    <Label>Ajouter les frais de transaction au client</Label>
                  </div>
                  {settings.mypos_add_fees_to_client && (
                    <div className="grid gap-2 max-w-xs">
                      <Label htmlFor="mypos_fee_percentage">Pourcentage de frais (%)</Label>
                      <Input
                        id="mypos_fee_percentage"
                        type="number"
                        step="0.1"
                        value={settings.mypos_fee_percentage}
                        onChange={(e) => setSettings(prev => ({ ...prev, mypos_fee_percentage: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={handleTestConnection} variant="outline" disabled={testing}>
                  {testing ? (
                    <FiRefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FiWifi className="h-4 w-4 mr-2" />
                  )}
                  Tester la connexion
                </Button>
                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? (
                    <FiRefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FiSave className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
                <a 
                  href="https://developers.mypos.com/apis/ecommerce/link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline ml-auto"
                >
                  Documentation MyPOS
                  <FiExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Transfer Configuration */}
        <TabsContent value="bank">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <FiDollarSign className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>Virement Bancaire</CardTitle>
                    <CardDescription>Coordonnées bancaires pour les paiements par virement</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings.bank_transfer_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, bank_transfer_enabled: checked }))}
                  />
                  <span className="text-sm text-gray-500">{settings.bank_transfer_enabled ? 'Activé' : 'Désactivé'}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="bank_account_holder">Titulaire du compte</Label>
                  <Input
                    id="bank_account_holder"
                    placeholder="Nom du titulaire"
                    value={settings.bank_account_holder}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_account_holder: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bank_name">Nom de la banque</Label>
                  <Input
                    id="bank_name"
                    placeholder="Ex: Crédit Agricole"
                    value={settings.bank_name}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_name: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bank_iban">IBAN</Label>
                  <Input
                    id="bank_iban"
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    value={settings.bank_iban}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_iban: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bank_bic">BIC/SWIFT</Label>
                  <Input
                    id="bank_bic"
                    placeholder="XXXXXXXX"
                    value={settings.bank_bic}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_bic: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? (
                    <FiRefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FiSave className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deposit Settings */}
        <TabsContent value="deposit">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <FiPercent className="h-6 w-6 text-violet-600" />
                </div>
                <div>
                  <CardTitle>Paramètres d'Acompte</CardTitle>
                  <CardDescription>Configurez le pourcentage d'acompte par défaut pour les réservations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Switch
                  checked={settings.deposit_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, deposit_enabled: checked }))}
                />
                <Label>Demander un acompte pour les réservations</Label>
              </div>
              
              {settings.deposit_enabled && (
                <div className="grid gap-4 max-w-md">
                  <div className="grid gap-2">
                    <Label htmlFor="deposit_percentage">Pourcentage d'acompte (%)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="deposit_percentage"
                        type="number"
                        min="1"
                        max="100"
                        value={settings.deposit_percentage}
                        onChange={(e) => setSettings(prev => ({ ...prev, deposit_percentage: e.target.value }))}
                        className="max-w-[100px]"
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Par défaut, {settings.deposit_percentage}% du montant total sera demandé en acompte.
                    </p>
                  </div>
                  
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                    <h4 className="font-medium text-violet-800 mb-2">Exemple de calcul</h4>
                    <p className="text-sm text-violet-700">
                      Pour une prestation à <strong>500€</strong>, l'acompte sera de <strong>{(500 * parseFloat(settings.deposit_percentage || 30) / 100).toFixed(2)}€</strong>
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? (
                    <FiRefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FiSave className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiLink className="h-5 w-5" />
            Transactions Récentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucune transaction pour le moment
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Commande</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Montant</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Statut</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Lien</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm">{tx.order_id}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          tx.payment_type === 'deposit' ? 'bg-blue-100 text-blue-700' :
                          tx.payment_type === 'balance' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {tx.payment_type === 'deposit' ? 'Acompte' : 
                           tx.payment_type === 'balance' ? 'Solde' : 
                           'Carte Cadeau'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">{tx.customer_name}</div>
                        <div className="text-xs text-gray-400">{tx.customer_email}</div>
                      </td>
                      <td className="py-3 px-4 font-medium">
                        {formatAmount(tx.amount)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(tx.status)}`}>
                          {getStatusLabel(tx.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4">
                        {tx.payment_link_url && tx.status === 'pending' && (
                          <a 
                            href={tx.payment_link_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                          >
                            Voir <FiExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
